/**
 * @fileoverview PatrolPuffin Terms of Service registry data.
 *
 * Hand-migrated faithfully from
 * `apps/patrol-web/src/portals/marketing-website/pages/TermsOfServicePage.jsx`.
 */

/** @type {import('../../types.js').LegalDocument} */
export const terms = Object.freeze({
  appKey: 'patrol',
  documentKey: 'terms',
  frontmatter: {
    title: 'Terms of Service',
    productName: 'PatrolPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-02-28',
    contactEmail: 'legal@patrolpuffin.com',
    canonicalUrl: 'https://patrolpuffin.com/terms',
    ogImage: 'https://patrolpuffin.com/og-image.jpg',
    description:
      'PatrolPuffin terms of service. Review the terms governing use of our automated carrier invoice auditing and shipping cost recovery platform.',
  },
  sections: [
    {
      id: 'agreement-to-terms',
      title: '1. Agreement to Terms',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'By accessing or using {productName}, you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use our services. These terms apply to all users, including businesses and individuals who access {productName} for carrier invoice auditing.',
        },
      ],
    },
    {
      id: 'description-of-service',
      title: '2. Description of Service',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} provides automated carrier invoice auditing for postal retailers. Our platform analyzes USPS, UPS, and FedEx invoices to detect billing discrepancies, overcharges, and refund opportunities. We identify up to 10 types of billing errors and assist with claim filing.',
        },
      ],
    },
    {
      id: 'account-registration',
      title: '3. Account Registration',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'You must provide accurate and complete information when creating an account. You are responsible for maintaining the security of your account credentials and for all activity that occurs under your account. You must notify us immediately of any unauthorized access.',
        },
      ],
    },
    {
      id: 'subscription-and-billing',
      title: '4. Subscription and Billing',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} is offered on a subscription basis with monthly or annual billing cycles. Prices are listed on our pricing page and may be updated with 30 days notice. You may cancel your subscription at any time. No refunds are provided for partial billing periods.',
        },
      ],
    },
    {
      id: 'acceptable-use',
      title: '5. Acceptable Use',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'You agree to use {productName} only for lawful purposes and in accordance with these terms. You may not use our services to submit fraudulent claims, provide false information to carriers, or engage in any activity that violates applicable laws or regulations.',
        },
      ],
    },
    {
      id: 'intellectual-property',
      title: '6. Intellectual Property',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'All content, features, and functionality of {productName}, including our auditing algorithms, user interface, and documentation, are owned by {productName} and protected by intellectual property laws. You may not copy, modify, or distribute our proprietary technology.',
        },
      ],
    },
    {
      id: 'data-and-privacy',
      title: '7. Data and Privacy',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'Your use of {productName} is also governed by our Privacy Policy, which describes how we collect, use, and protect your data. By using our services, you consent to the data practices described in our Privacy Policy.',
        },
      ],
    },
    {
      id: 'limitation-of-liability',
      title: '8. Limitation of Liability',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} provides auditing recommendations and claim assistance, but does not guarantee specific savings amounts or claim outcomes. Carrier decisions on refunds and credits are made by the carriers themselves. Our liability is limited to the amount you paid for the service in the preceding 12 months.',
        },
      ],
    },
    {
      id: 'termination',
      title: '9. Termination',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may suspend or terminate your access to {productName} if you violate these terms. You may cancel your account at any time. Upon termination, your right to use the service ceases immediately, though we will retain data as described in our Privacy Policy.',
        },
      ],
    },
    {
      id: 'changes-to-terms',
      title: '10. Changes to Terms',
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: 'We reserve the right to modify these terms at any time. We will provide notice of material changes via email or through our platform. Continued use of {productName} after changes take effect constitutes acceptance of the revised terms.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '11. Contact Us',
      order: 11,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about these Terms of Service, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
